import type { MeResponse, Profile, UpdateProfileInput } from '@onflow/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api, refreshAccessToken } from '../lib/api.js';
import { useAuthStore } from '../stores/authStore.js';

/**
 * Bootstraps auth on mount: tries to refresh the session using the httpOnly
 * cookie and, on success, fetches /auth/me so the store holds the hydrated
 * identity by the time ProtectedRoute renders its children. This removes the
 * separate loading flicker on every page that needs the user's name.
 */
export function useBootstrapAuth(): { status: 'unknown' | 'anonymous' | 'authenticated' } {
  const status = useAuthStore((s) => s.status);
  const clear = useAuthStore((s) => s.clear);
  const setIdentity = useAuthStore((s) => s.setIdentity);

  useEffect(() => {
    if (status !== 'unknown') return;
    void (async () => {
      const token = await refreshAccessToken();
      if (!token) {
        clear();
        return;
      }
      try {
        const me = await api<MeResponse>('/auth/me');
        setIdentity(me);
      } catch {
        clear();
      }
    })();
  }, [status, clear, setIdentity]);

  return { status };
}

export function useLogout(): () => Promise<void> {
  const clear = useAuthStore((s) => s.clear);
  return async () => {
    try {
      await api('/auth/logout', { method: 'POST', auth: false });
    } finally {
      clear();
    }
  };
}

export function useMe() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['me'],
    enabled: Boolean(accessToken),
    queryFn: () => api<MeResponse>('/auth/me'),
  });
}

/**
 * PATCH /v1/me/profile — user preferences (units, rest-timer default, …).
 * Seeds the ['me'] cache from the response so the Profile screen reflects the
 * new value without a refetch round-trip, and syncs the auth store so screens
 * reading `profile` from zustand (the workout session's rest default) pick it
 * up too.
 */
export function useUpdateProfile() {
  const qc = useQueryClient();
  const setIdentity = useAuthStore((s) => s.setIdentity);
  const me = useAuthStore((s) => s.user);
  return useMutation<Profile, Error, UpdateProfileInput>({
    mutationFn: (input) => api<Profile>('/me/profile', { method: 'PATCH', json: input }),
    onSuccess: (profile) => {
      qc.setQueryData<MeResponse>(['me'], (prev) => (prev ? { ...prev, profile } : prev));
      if (me) setIdentity({ user: me, profile });
    },
  });
}
